import { DispatchStep, IntegrationStepConfig, TriggerStep } from '@/types/automationSteps.ts';
import { Box, Stack } from '@mantine/core';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useStepIntegration from '@/hooks/useStepIntegration.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { StepType } from '@/types/automationFields.ts';
import IntegrationConfigFields from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationConfigFields.tsx';
import { IntegrationSelect } from '@/components/IntegrationSelect.tsx';
import { IntegrationInstance, IntegrationProvider } from '@/types/integrations.ts';
import {
  isStandaloneCapabilityProvider,
  providerUsesIntegrationInstance,
} from '@/utils/providerKinds.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import useCompositionOperations from '@/hooks/api/automations/useCompositionOperations.ts';
import type { BrowserSecretBinding, CompositionOperation } from '@/hooks/api/automations/useCompositionOperations.ts';
import useCustomDebouncedCallback from '@/hooks/useCustomDebouncedCallback.ts';
import { withBrowserModelSelection } from './browserModelSelection.ts';

type props = {
  step: TriggerStep | DispatchStep;
};

type Fields = {
  integration_config: IntegrationStepConfig;
};

const getProviderActions = (
  provider: IntegrationProvider | undefined,
  stepType: StepType.Trigger | StepType.Dispatch,
) => {
  return (
    (stepType === StepType.Trigger ? provider?.trigger_actions : provider?.dispatch_actions) || []
  );
};

const parseBrowserSecretBindings = (value: unknown): BrowserSecretBinding[] | null => {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (
      !Array.isArray(parsed)
      || !parsed.every(
        (binding) =>
          binding
          && typeof binding === 'object'
          && typeof binding.alias === 'string'
          && typeof binding.sensitive_key === 'string'
          && Array.isArray(binding.allowed_domains)
          && binding.allowed_domains.every((domain: unknown) => typeof domain === 'string'),
      )
    ) {
      return null;
    }
    return parsed as BrowserSecretBinding[];
  } catch {
    return null;
  }
};

const IntegrationFieldsContainer = ({ step }: props) => {
  const {
    fields,
    onChange: onChangeDelay,
    onChangeNoDelay: onChangeNoDelay,
  } = useUpdateStepFields<Fields>({ step, syncWithAPI: true });
  const { target } = useActiveCompositionTarget();
  const { mutate: applyComposition } = useCompositionOperations();
  const subflowId = target.scope_kind === 'local_subflow' ? target.subflow_id : undefined;
  const isModuleStep = Boolean(subflowId);
  const [scopedFields, setScopedFields] = useState<Fields>({ integration_config: step.integration_config });
  const persistedScopedConfigRef = useRef(step.integration_config);

  useEffect(() => {
    if (isModuleStep) {
      setScopedFields({ integration_config: step.integration_config });
      persistedScopedConfigRef.current = step.integration_config;
    }
  }, [isModuleStep, step.integration_config]);

  const editorFields = isModuleStep ? scopedFields : fields;

  const applyScopedIntegrationConfig = useCallback(
    (updatedConfig: IntegrationStepConfig) => {
      if (!subflowId) {
        return;
      }
      const currentConfig = persistedScopedConfigRef.current;
      const selectionChanged = (
        currentConfig.integration_id !== updatedConfig.integration_id
        || currentConfig.action_type !== updatedConfig.action_type
        || currentConfig.integration_type !== updatedConfig.integration_type
      );
      if (selectionChanged) {
        applyComposition([{
          op_type: 'replace_integration_selection',
          subflow_id: subflowId,
          step_id: step.step_id,
          integration_id: updatedConfig.integration_id,
          action_type: updatedConfig.action_type,
        }], {
          onSuccess: () => {
            persistedScopedConfigRef.current = updatedConfig;
          },
        });
        return;
      }

      const currentFields = Object.fromEntries(
        currentConfig.config_values.fields.map((field) => [field.field_id, field.value]),
      );
      const nextFields = Object.fromEntries(
        updatedConfig.config_values.fields.map((field) => [field.field_id, field.value]),
      );
      const changedFields = Object.fromEntries(
        Object.entries(nextFields).filter(([fieldId, value]) => (
          JSON.stringify(currentFields[fieldId]) !== JSON.stringify(value)
        )),
      );
      const operations: CompositionOperation[] = [];
      const browserSecretBindings = changedFields.browser_secret_bindings;
      if (browserSecretBindings !== undefined) {
        const bindings = parseBrowserSecretBindings(browserSecretBindings);
        if (bindings === null) {
          return;
        }
        operations.push({
          op_type: 'replace_browser_secret_binding_contract',
          subflow_id: subflowId,
          step_id: step.step_id,
          value: bindings,
        });
        delete changedFields.browser_secret_bindings;
      }
      if (Object.keys(changedFields).length > 0) {
        operations.push({
          op_type: 'update_integration_config_fields',
          subflow_id: subflowId,
          step_id: step.step_id,
          config_fields: changedFields,
        });
      }
      if (operations.length > 0) {
        applyComposition(operations, {
          onSuccess: () => {
            persistedScopedConfigRef.current = updatedConfig;
          },
        });
      }
    },
    [applyComposition, step.step_id, subflowId],
  );
  const updateScopedIntegrationConfigDelayed = useCustomDebouncedCallback(
    (updatedConfig: IntegrationStepConfig) => applyScopedIntegrationConfig(updatedConfig),
    [applyScopedIntegrationConfig],
  );

  const {
    loadingIntegrations,
    selectedIntegrationProvider,
    selectedIntegrationAction,
    selectedIntegrationInstance,
  } = useStepIntegration(
    step.step_type,
    editorFields.integration_config.integration_id,
    editorFields.integration_config.action_type,
  );

  const integrationError = useStepSpecificError(step, StepErrorType.InvalidIntegration);
  const actionError = useStepSpecificError(step, StepErrorType.InvalidIntegrationAction);

  const onChangeIntegrationConfigNoDelay = useCallback(
    (updatedConfig: IntegrationStepConfig) => {
      if (isModuleStep) {
        setScopedFields({ integration_config: updatedConfig });
        applyScopedIntegrationConfig(updatedConfig);
      } else {
        onChangeNoDelay({ integration_config: updatedConfig });
      }
    },
    [applyScopedIntegrationConfig, isModuleStep, onChangeNoDelay],
  );

  const onChangeIntegrationConfigDelay = useCallback(
    (updatedConfig: IntegrationStepConfig) => {
      if (isModuleStep) {
        setScopedFields({ integration_config: updatedConfig });
        updateScopedIntegrationConfigDelayed(updatedConfig);
      } else {
        onChangeDelay({ integration_config: updatedConfig });
      }
    },
    [isModuleStep, onChangeDelay, updateScopedIntegrationConfigDelayed],
  );

  const showIntegrationAndActionSelectors = useMemo(
    () =>
      selectedIntegrationProvider
        ? !isStandaloneCapabilityProvider(selectedIntegrationProvider)
        : true,
    [selectedIntegrationProvider],
  );

  const updateIntegration = useCallback(
    (p?: IntegrationProvider, i?: IntegrationInstance, a?: string | null) => {
      if (!p) return;
      const actions = getProviderActions(p, step.step_type);
      if (!a && actions.length === 1) {
        a = actions[0].name;
      }
      const integrationId = providerUsesIntegrationInstance(p) ? i?.id || null : p.name;
      onChangeIntegrationConfigNoDelay({
        integration_type: p.name,
        integration_id: integrationId,
        action_type: a || null,
        config_values: { fields: [] },
      });
    },
    [onChangeIntegrationConfigNoDelay, step.step_type],
  );

  return (
    <Stack w={'100%'}>
      {showIntegrationAndActionSelectors && (
        <>
          <Box pos="relative" w={'100%'}>
            <CustomLoadingOverlay visible={loadingIntegrations} />

            <IntegrationSelect
              onSelect={(p, i) => updateIntegration(p, i, undefined)}
              provider={selectedIntegrationProvider}
              instance={selectedIntegrationInstance}
              selectType={step.step_type === StepType.Trigger ? 'trigger' : 'dispatch'}
              integrationError={integrationError}
              label="Integration"
            />
          </Box>

          <Box pos="relative">
            <StyledSelect
              label="Action"
              placeholder="Select an action"
              data={getProviderActions(selectedIntegrationProvider, step.step_type).map(
                (action) => ({ value: action.name, label: action.display_name }),
              )}
              value={selectedIntegrationAction?.name}
              onChange={(value) =>
                updateIntegration(selectedIntegrationProvider, selectedIntegrationInstance, value)
              }
              disabled={!selectedIntegrationProvider}
              required={true}
              allowDeselect={false}
              clearable={false}
              error={actionError}
            />
          </Box>
        </>
      )}

      {!integrationError &&
        !actionError &&
        selectedIntegrationAction &&
        withBrowserModelSelection(
          editorFields.integration_config,
          selectedIntegrationAction.config,
        ).config_values.fields.length > 0 && (
          <IntegrationConfigFields
            step={step}
            stepConfig={editorFields.integration_config}
            actionConfig={selectedIntegrationAction.config}
            onChangeIntegrationConfigNoDelay={onChangeIntegrationConfigNoDelay}
            onChangeIntegrationConfigDelay={onChangeIntegrationConfigDelay}
            showAccordion={showIntegrationAndActionSelectors}
            allowSensitiveFields={!isModuleStep}
          />
        )}
    </Stack>
  );
};

export default IntegrationFieldsContainer;
